import { useState } from "react"
import { Check, Plus, RotateCcw, SlidersHorizontal } from "lucide-react"

import { Button } from "@ibada/ui"

import { DEFAULT_DASHBOARD_ENGINE_LABELS, useDashboardEngineLabels } from "../model/engine-labels"

const DEFAULT_RESET_LABEL = DEFAULT_DASHBOARD_ENGINE_LABELS.reset

import type { UseDashboardLayoutResult } from "../hooks/use-dashboard-layout"
import { AddWidgetsDialog } from "./add-widgets-dialog"

export interface DashboardCustomizeToolbarProps {
  dashboard: UseDashboardLayoutResult
  /** Show a "Reset to default" action in customize mode. Opt-in: boards without a designed default omit it. */
  allowReset?: boolean
}

/**
 * Right-aligned dashboard controls. Normal mode shows a single "Customize" button; customize mode
 * swaps to "Add widgets" (opens the restore dialog) + Done, plus an aria-live status so the mode is
 * announced to assistive tech. Done is the only exit — it commits the current layout.
 */
export function DashboardCustomizeToolbar({ dashboard, allowReset = false }: DashboardCustomizeToolbarProps) {
  const labels = useDashboardEngineLabels()
  const { editMode, enterEditMode, done, hiddenWidgets, restoreWidget } = dashboard
  const [addOpen, setAddOpen] = useState(false)

  if (!editMode) {
    return (
      <Button variant="outline" size="sm" onClick={enterEditMode}>
        <SlidersHorizontal className="size-4" />
        {labels.configure}
      </Button>
    )
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span role="status" aria-live="polite" className="sr-only">
        {labels.editing}
      </span>
      {allowReset ? (
        <Button variant="ghost" size="sm" onClick={dashboard.reset}>
          <RotateCcw className="size-4" />
          {labels.reset ?? DEFAULT_RESET_LABEL}
        </Button>
      ) : null}
      <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
        <Plus className="size-4" />
        {labels.addWidgets}
        {hiddenWidgets.length > 0 ? (
          <span className="ml-0.5 tabular-nums text-muted-foreground">({hiddenWidgets.length})</span>
        ) : null}
      </Button>
      <Button size="sm" onClick={done}>
        <Check className="size-4" />
        {labels.done}
      </Button>

      <AddWidgetsDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        hiddenWidgets={hiddenWidgets}
        onRestore={restoreWidget}
      />
    </div>
  )
}
